import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useMemo, useState } from 'react';

import { DisabledTooltip } from '@commons/DisabledTooltip';
import { formatNumber, formatNumberWithCurrency } from '@commons/DisplayNumber';
import { HoverTooltip, ToggleButton, Tooltip } from '@commons/utils/styledLibraryComponents';
import GeneralEmptyStateListView from '@commons/GeneralEmptyStateListView';
import Text, { ENUM_COLORS, ENUM_FONTS, OVERFLOW_STYLE_TEXT } from '@commons/Text';

import {
  CentralKitchenCell,
  CentralKitchenName,
  Container,
  MetricCell,
  PackagingCell,
  SortableHeader,
  SortIcon,
  SyncBadge,
  SyncIcon,
  SyncIconSlot,
  Table,
  TableHeaderRow,
  TableRow,
  TitleRow,
  ToggleContainer,
} from './styledComponents';
import { computeCentralKitchenMetrics } from './utils/computeCentralKitchenMetrics';

const EMPTY_STATE_ICON = '/images/inpulse-icon-no-data.svg';
const SUPPLIER_PRODUCT_DETAILS_PATH = '/admin/suppliers/supplier-products';
const SYNC_SOURCE_ICON = '/images/inpulse/sync-black-small.svg';
const SORT_ICON_ACTIVE = '/images/inpulse/sort-default-black-small.svg';
const SORT_ICON_INACTIVE = '/images/inpulse/sort-default-dgrey-small.svg';

const METRIC_PLACEHOLDER = '—';

const DISPLAY_MODE = {
  PERCENT: 'PERCENT',
  CURRENCY: 'CURRENCY',
};

const SORT_COLUMNS = {
  CENTRAL_KITCHEN: 'centralKitchen',
  INVOICE_PACKAGING: 'invoicePackaging',
  COST: 'cost',
  PRICE: 'price',
  MARGIN: 'margin',
  RATIO: 'ratio',
};

const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
};

const formatMetricAsCurrency = (value, currency) => {
  if (value == null) {
    return METRIC_PLACEHOLDER;
  }

  return formatNumberWithCurrency({ number: value, currency, displayCurrencyCode: true });
};

const formatMetricAsPercentage = (value, numberDecimals) => {
  if (value == null) {
    return METRIC_PLACEHOLDER;
  }

  return `${formatNumber(value, numberDecimals)} %`;
};

/**
 * Reads the value each column sorts by. Margin and ratio follow the current %/currency toggle so
 * clicking a header always sorts by the number currently displayed in that column.
 */
const getSortValue = (row, sortColumn, isPercentMode) => {
  const { metrics } = row;

  switch (sortColumn) {
    case SORT_COLUMNS.CENTRAL_KITCHEN:
      return row.centralKitchenName.toLowerCase();
    case SORT_COLUMNS.INVOICE_PACKAGING:
      return (row.invoicePackagingName || '').toLowerCase();
    case SORT_COLUMNS.COST:
      return row.metrics.costExTax;
    case SORT_COLUMNS.PRICE:
      return row.metrics.priceExTax;
    case SORT_COLUMNS.MARGIN:
      return isPercentMode ? metrics.margin : metrics.marginExTax;
    case SORT_COLUMNS.RATIO:
      return isPercentMode ? metrics.ratio : metrics.costExTax;
    default:
      return null;
  }
};

/**
 * Displays the cost/price/margin/ratio of the product for each associated central kitchen, all
 * expressed on each supplier product's invoice packaging. Only the central kitchens supplying the
 * product through an active supplier product are listed, and they are ranked by ascending margin
 * by default so the worst performing ones surface first.
 */
const KitchenProductCentralKitchensPerformance = (props) => {
  const { composition, currency, defaultChannelId, supplierProducts } = props;

  const activeSupplierProducts = useMemo(
    () => supplierProducts.filter(({ active }) => !!active),
    [supplierProducts],
  );

  const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.PERCENT);
  const [sortColumn, setSortColumn] = useState(SORT_COLUMNS.CENTRAL_KITCHEN);
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTIONS.ASC);

  const isPercentMode = displayMode === DISPLAY_MODE.PERCENT;

  const displayModeChoices = useMemo(
    () => [
      {
        key: DISPLAY_MODE.PERCENT,
        name: i18next.t('ADMIN.RECIPES.PERFORMANCES_TOGGLE_RATIO'),
      },
      {
        key: DISPLAY_MODE.CURRENCY,
        name: i18next.t('ADMIN.RECIPES.PERFORMANCES_TOGGLE_COST', {
          currencyCode: currency.alphabeticCode,
        }),
      },
    ],
    [currency.alphabeticCode],
  );

  const selectedDisplayModeChoice = displayModeChoices.find(({ key }) => key === displayMode);

  const rows = useMemo(() => {
    const metricsBySupplierProductId = computeCentralKitchenMetrics({
      composition,
      defaultChannelId,
      supplierProducts: activeSupplierProducts,
    });

    return activeSupplierProducts.map((supplierProduct) => {
      const invoicePackaging = (supplierProduct.packagings || []).find(
        ({ isUsedInInvoice }) => isUsedInInvoice,
      );

      return {
        id: supplierProduct.id,
        centralKitchenName: supplierProduct.supplierName,
        invoicePackagingName: invoicePackaging?.name ?? null,
        isSyncSource: !!supplierProduct.productMapping?.usedByDefault,
        metrics: metricsBySupplierProductId[supplierProduct.id],
      };
    });
  }, [composition, defaultChannelId, activeSupplierProducts]);

  const sortedRows = useMemo(() => {
    const direction = sortDirection === SORT_DIRECTIONS.ASC ? 1 : -1;

    return [...rows].sort((rowA, rowB) => {
      const valueA = getSortValue(rowA, sortColumn, isPercentMode);
      const valueB = getSortValue(rowB, sortColumn, isPercentMode);

      if (typeof valueA === 'string' || typeof valueB === 'string') {
        return direction * String(valueA).localeCompare(String(valueB));
      }

      // Rows without a computable metric always sink to the bottom of the list
      if (valueA == null && valueB == null) {
        return 0;
      }

      if (valueA == null) {
        return 1;
      }

      if (valueB == null) {
        return -1;
      }

      return direction * (valueA - valueB);
    });
  }, [rows, sortColumn, sortDirection, isPercentMode]);

  const handleSortChange = (column) => {
    if (column === sortColumn) {
      setSortDirection(
        sortDirection === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC,
      );
      return;
    }

    setSortColumn(column);
    setSortDirection(SORT_DIRECTIONS.ASC);
  };

  const renderSortableHeader = (column, label) => {
    const isActiveSortColumn = sortColumn === column;

    return (
      <SortableHeader onClick={() => handleSortChange(column)}>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_SMALL}>
          {label}
        </Text>
        <SortIcon
          $isDescending={isActiveSortColumn && sortDirection === SORT_DIRECTIONS.DESC}
          src={isActiveSortColumn ? SORT_ICON_ACTIVE : SORT_ICON_INACTIVE}
        />
      </SortableHeader>
    );
  };

  const renderMetric = (percentValue, currencyValue) => (
    <Text color={ENUM_COLORS.BLACK} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
      {isPercentMode
        ? formatMetricAsPercentage(percentValue, currency.numberDecimals)
        : formatMetricAsCurrency(currencyValue, currency)}
    </Text>
  );

  return (
    <Container>
      <TitleRow>
        <Text font={ENUM_FONTS.H2}>{i18next.t('ADMIN.RECIPES.PERFORMANCES')}</Text>
        {!isEmpty(activeSupplierProducts) && (
          <ToggleContainer>
            <ToggleButton
              choices={displayModeChoices}
              selectedChoice={selectedDisplayModeChoice}
              setSelectedChoice={({ key }) => setDisplayMode(key)}
            />
          </ToggleContainer>
        )}
      </TitleRow>
      {isEmpty(activeSupplierProducts) ? (
        <GeneralEmptyStateListView
          icon={EMPTY_STATE_ICON}
          subtitle={i18next.t(
            'ADMIN.CENTRAL_PRODUCTS.ASSOCIATED_CENTRAL_KITCHENS_EMPTY_STATE_SUBTITLE',
          )}
          title={i18next.t('ADMIN.CENTRAL_PRODUCTS.ASSOCIATED_CENTRAL_KITCHENS_EMPTY_STATE_TITLE')}
        />
      ) : (
        <Table>
          <TableHeaderRow>
            <CentralKitchenCell>
              <SyncIconSlot />
              {renderSortableHeader(SORT_COLUMNS.CENTRAL_KITCHEN, i18next.t('GENERAL.CENTRAL'))}
            </CentralKitchenCell>
            <PackagingCell>
              {renderSortableHeader(
                SORT_COLUMNS.INVOICE_PACKAGING,
                i18next.t('ADMIN.SUPPLIER_PRODUCTS.INVOICE_PACKAGING'),
              )}
            </PackagingCell>
            <MetricCell>
              {renderSortableHeader(SORT_COLUMNS.COST, i18next.t('GENERAL.COST_EX_TAX'))}
            </MetricCell>
            <MetricCell>
              {renderSortableHeader(SORT_COLUMNS.PRICE, i18next.t('GENERAL.PRICE_EX_TAX'))}
            </MetricCell>
            <MetricCell>
              {renderSortableHeader(
                SORT_COLUMNS.MARGIN,
                i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_WITHOUT_CURRENCY'),
              )}
            </MetricCell>
            <MetricCell>
              {renderSortableHeader(
                SORT_COLUMNS.RATIO,
                i18next.t('ADMIN.RECIPES.PERFORMANCES_TOOLTIP_RATIO'),
              )}
            </MetricCell>
          </TableHeaderRow>
          {sortedRows.map(
            ({ id, centralKitchenName, invoicePackagingName, isSyncSource, metrics }) => (
              <TableRow key={id}>
                <CentralKitchenCell>
                  <SyncIconSlot>
                    {isSyncSource && (
                      <HoverTooltip
                        renderContent={() => (
                          <DisabledTooltip
                            text={i18next.t('ADMIN.CENTRAL_PRODUCTS.SYNC_SOURCE_TOOLTIP')}
                          />
                        )}
                        isTooltipDisplayed
                      >
                        <SyncBadge>
                          <SyncIcon src={SYNC_SOURCE_ICON} />
                        </SyncBadge>
                      </HoverTooltip>
                    )}
                  </SyncIconSlot>
                  {/* The row is named after the central kitchen, but it stands for its supplier product */}
                  <CentralKitchenName
                    onClick={() =>
                      window.open(`${SUPPLIER_PRODUCT_DETAILS_PATH}/${id}/details`, '_blank')
                    }
                  >
                    <Tooltip
                      renderTooltipTrigger={() => (
                        <Text
                          color={ENUM_COLORS.BLACK}
                          font={ENUM_FONTS.TEXT_SMALL_BOLD}
                          style={OVERFLOW_STYLE_TEXT}
                        >
                          {centralKitchenName}
                        </Text>
                      )}
                      text={centralKitchenName}
                      displayBigger
                    />
                  </CentralKitchenName>
                </CentralKitchenCell>
                <PackagingCell>
                  <Text
                    color={ENUM_COLORS.BLACK}
                    font={ENUM_FONTS.TEXT_SMALL}
                    style={OVERFLOW_STYLE_TEXT}
                  >
                    {invoicePackagingName ?? METRIC_PLACEHOLDER}
                  </Text>
                </PackagingCell>
                <MetricCell>
                  <Text color={ENUM_COLORS.BLACK} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
                    {formatMetricAsCurrency(metrics.costExTax, currency)}
                  </Text>
                </MetricCell>
                <MetricCell>
                  <Text color={ENUM_COLORS.BLACK} font={ENUM_FONTS.TEXT_SMALL_BOLD}>
                    {formatMetricAsCurrency(metrics.priceExTax, currency)}
                  </Text>
                </MetricCell>
                <MetricCell>{renderMetric(metrics.margin, metrics.marginExTax)}</MetricCell>
                <MetricCell>{renderMetric(metrics.ratio, metrics.costExTax)}</MetricCell>
              </TableRow>
            ),
          )}
        </Table>
      )}
    </Container>
  );
};

export default KitchenProductCentralKitchensPerformance;
